import React from 'react';
import { Link } from 'react-router-dom';
import { useAnalytics, useLinkedAgents } from '../hooks/useAnalytics';
import StatCard from '../components/StatCard';

import { statsIcons } from '../components/ui/StatsIcons';
import { navigationIcons } from '../components/ui/NavigationIcons';
import { useTheme } from '../context/ThemeContext';

const Dashboard: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { data: linkedAgents, isLoading: linkedAgentsLoading } = useLinkedAgents();
  const { data: analytics, isLoading: analyticsLoading, error } = useAnalytics();

  const loading = linkedAgentsLoading || analyticsLoading;

  const formatDuration = (seconds: number | null) => {
    if (seconds === null || seconds === undefined) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `$${amount.toFixed(4)}`;
  };

  const getMetricValue = (key: string) => {
    if (!analytics?.metrics) return null;
    const metric = analytics.metrics.find(m => m.key === key);
    return metric?.value ?? null;
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Dashboard</h1>
        </div>
        <div className="text-center py-8">
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Dashboard</h1>
        </div>
        <div
          className={`border rounded-md p-4 ${isDarkMode
            ? 'bg-red-900/20 border-red-800 text-red-300'
            : 'bg-red-50 border-red-200 text-red-800'
            }`}
        >
          <p>{`Error: ${error}`}</p>
        </div>
      </div>
    );
  }

  // Empty state - no agents linked
  if (analytics?.is_empty || (!linkedAgents || linkedAgents.length === 0)) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Dashboard</h1>
        </div>

        {/* Empty State Card */}
        <div className={`rounded-2xl border p-8 text-center ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
            }`}>
            <svg className={`w-8 h-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Welcome to your Dashboard
          </h3>
          <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Link an assistant or workflow to start viewing analytics and call data.
          </p>
          <Link
            to="/agents"
            className={`inline-flex items-center px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${isDarkMode
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
          >
            Link Your First Agent
          </Link>
        </div>
      </div>
    );
  }

  // Main dashboard with analytics
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header Section */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={`text-xl sm:text-2xl font-semibold tracking-tight mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Dashboard
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Welcome back! Here's what's happening with your voice assistant.
          </p>
        </div>
        <Link
          to="/calls"
          className="btn-primary inline-flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-medium"
          aria-label="Navigate to call logs page"
        >
          <span>View Call Logs</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      {/* Key Metrics Section */}
      <section
        aria-labelledby="analytics-heading"
        className={`
          rounded-2xl border shadow-sm p-5 sm:p-6 animate-slide-in-left
          ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}
        `}
        style={{ animationDelay: '100ms' }}
      >
        {/* Section Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h2 id="analytics-heading" className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Key Metrics
            </h2>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Performance overview for your voice assistant
            </p>
          </div>
          {/* <div className={`
            inline-flex items-center text-xs font-medium px-3 py-1 rounded-full border
            ${isDarkMode ? 'text-gray-300 bg-gray-800 border-gray-600' : 'text-gray-600 bg-gray-50 border-gray-200'}
          `}>
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            Live Data
          </div> */}
        </div>

        {/* Metrics Grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
          role="group"
          aria-label="Key performance metrics"
        >
          <StatCard
            title="Calls Dropped by Customer"
            value={getMetricValue('calls_dropped_by_customer') ?? 0}
            icon={statsIcons.callsDropped}
            colorType="negative"
            trend="down"
            trendValue="2.1%"
            className="animate-scale-in"
            style={{ animationDelay: '150ms' }}
          />

          <StatCard
            title="Calls Ended by Agent"
            value={getMetricValue('calls_ended_by_agent') ?? 0}
            icon={statsIcons.callsEnded}
            colorType="positive"
            trend="up"
            trendValue="5.3%"
            className="animate-scale-in"
            style={{ animationDelay: '200ms' }}
          />

          <StatCard
            title="Average Duration"
            value={formatDuration(getMetricValue('average_duration_seconds') as number)}
            icon={statsIcons.averageDuration}
            colorType="neutral"
            trend="up"
            trendValue="12s"
            className="animate-scale-in"
            style={{ animationDelay: '250ms' }}
          />

          {/* <StatCard
            title="Average Cost"
            value={formatCurrency(getMetricValue('average_cost') as number)}
            icon={statsIcons.averageCost}
            colorType="positive"
            trend="down"
            trendValue="$0.03"
            className="animate-scale-in"
            style={{ animationDelay: '300ms' }}
          /> */}
        </div>

        {/* Summary Footer */}
        <div className={`
          mt-6 pt-4 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2
          ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}
        `}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Metrics are updated in real-time and reflect your linked agents
          </p>
          <div className={`text-xs font-medium px-3 py-1 rounded-full ${isDarkMode ? 'text-green-400 bg-green-900/20' : 'text-green-600 bg-green-50'
            }`}>
            ● Live Data
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section
        aria-labelledby="quick-actions-heading"
        className={`
          rounded-2xl border shadow-sm p-5 sm:p-6
          ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}
        `}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <h2 id="quick-actions-heading" className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Quick Actions
          </h2>
          <div className={`text-xs font-medium px-3 py-1 rounded-full ${isDarkMode ? 'text-gray-400 bg-gray-800' : 'text-gray-500 bg-gray-100'
            }`}>
            Shortcuts
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6" role="group" aria-label="Quick action shortcuts">
          {/* View Call Logs Card */}
          <Link
            to="/calls"
            className={`
              group rounded-2xl border p-4 sm:p-5
              transition-all duration-200 ease-in-out
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
              ${isDarkMode
                ? 'border-gray-700 bg-gray-800 hover:border-gray-600 hover:bg-gray-700'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
              }
            `}
            aria-label="Navigate to call logs to browse and search through all calls"
          >
            <div className="flex items-start gap-4">
              <div className={`
                flex-shrink-0 p-3 rounded-xl
                ${isDarkMode
                  ? 'bg-primary-900/30'
                  : 'bg-primary-50'
                }
              `}>
                <navigationIcons.callLogs
                  className={`h-6 w-6 ${isDarkMode
                    ? 'text-primary-400'
                    : 'text-primary-600'
                    }`}
                  aria-hidden="true"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`text-base font-semibold ${isDarkMode
                  ? 'text-white'
                  : 'text-gray-900'
                  }`}>
                  View Call Logs
                </h3>
                <p className={`text-xs mt-1 leading-relaxed ${isDarkMode
                  ? 'text-gray-300'
                  : 'text-gray-500'
                  }`}>
                  Browse and search through all your calls with detailed filtering options
                </p>
              </div>
            </div>
          </Link>

          {/* Detailed Analytics Card */}
          <Link
            to="/analytics"
            className={`
              group rounded-2xl border p-4 sm:p-5
              transition-all duration-200 ease-in-out
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
              ${isDarkMode
                ? 'border-gray-700 bg-gray-800 hover:border-gray-600 hover:bg-gray-700'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
              }
            `}
            aria-label="Navigate to detailed analytics to view comprehensive performance metrics"
          >
            <div className="flex items-start gap-4">
              <div className={`
                flex-shrink-0 p-3 rounded-xl
                ${isDarkMode
                  ? 'bg-secondary-900/30'
                  : 'bg-secondary-50'
                }
              `}>
                <navigationIcons.analytics
                  className={`h-6 w-6 ${isDarkMode
                    ? 'text-secondary-400'
                    : 'text-secondary-600'
                    }`}
                  aria-hidden="true"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`text-base font-semibold ${isDarkMode
                  ? 'text-white'
                  : 'text-gray-900'
                  }`}>
                  Detailed Analytics
                </h3>
                <p className={`text-xs mt-1 leading-relaxed ${isDarkMode
                  ? 'text-gray-300'
                  : 'text-gray-500'
                  }`}>
                  View comprehensive performance metrics and insights for optimization
                </p>
              </div>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;